import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Space, Tag, Tooltip, Typography } from 'antd';
import {
  CalendarOutlined,
  EditOutlined,
  IdcardOutlined,
  InfoCircleOutlined,
  MailOutlined,
  MergeCellsOutlined,
  PhoneOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { DuplicateGroup, DuplicateContact } from '../../../../types/contact';
import { formatPhone } from '../../../../utils/phone';

const { Text } = Typography;

// Formato corto inline + completo en tooltip. Ayuda al admin a decidir
// rápidamente qué contacto conservar como primario (normalmente el más
// reciente — o el más antiguo si quiere mantener la fecha histórica).
const SHORT_FMT = 'DD/MM/YYYY';
const FULL_FMT  = 'DD/MM/YYYY HH:mm';

const formatShort = (raw: string | undefined): string =>
  raw ? dayjs(raw).format(SHORT_FMT) : '—';

const formatFull = (raw: string | undefined): string =>
  raw ? dayjs(raw).format(FULL_FMT) : '—';

/** Devuelve true cuando la fecha de modificación coincide con la de
 *  creación (igual segundo) — significa que el contacto no se ha
 *  tocado desde que se creó y no merece la pena enseñar las dos. */
const wasNeverModified = (c: DuplicateContact): boolean => {
  if (!c.created || !c.modified) return true;
  return dayjs(c.created).isSame(dayjs(c.modified), 'second');
};

interface DuplicateGroupCardProps {
  group: DuplicateGroup;
  onMerge: () => void;
}

interface MatchMeta {
  icon: React.ReactNode;
  color: string;          // ant tag color
  accent: string;         // hex accent for icon tile
  label: string;          // human label
}

const matchMeta = (field: string, t: (k: string, d?: any) => string): MatchMeta => {
  switch (field) {
    case 'name':
      return {
        icon: <UserOutlined />,
        color: 'orange',
        accent: '#FA8C16',
        label: t('contacts.field.name_surname', 'Nombre y apellidos'),
      };
    case 'id_number':
      return {
        icon: <IdcardOutlined />,
        color: 'purple',
        accent: '#722ED1',
        label: t('contacts.field.identification_number', 'NIF'),
      };
    default:
      return {
        icon: <InfoCircleOutlined />,
        color: 'default',
        accent: '#8c8c8c',
        label: field,
      };
  }
};

export const DuplicateGroupCard: React.FC<DuplicateGroupCardProps> = ({
  group,
  onMerge,
}) => {
  const { t } = useTranslation();
  const meta = matchMeta(group.match_field, t);

  return (
    <Card
      size="small"
      hoverable
      // Altura uniforme para que las cards de la misma fila estén
      // alineadas aunque tengan listas de contactos de tamaños distintos.
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      styles={{
        body: {
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          flex: 1,
        },
      }}
    >
      {/* Header: icono coloreado + tipo + valor + nº de duplicados */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 8,
            backgroundColor: `${meta.accent}1A`,
            color: meta.accent,
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          {meta.icon}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {meta.label}
          </div>
          <Tooltip title={group.match_value}>
            <Text strong ellipsis style={{ fontSize: 14, display: 'block' }}>
              {group.match_value}
            </Text>
          </Tooltip>
        </div>
        <Tag color={meta.color} style={{ margin: 0 }}>
          {group.contacts.length} {t('admin.contacts.duplicate_count_short', 'dup.')}
        </Tag>
      </div>

      {/* Lista compacta de contactos */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          flex: 1,
          overflowY: 'auto',
          maxHeight: 260,
        }}
      >
        {group.contacts.map((contact) => {
          const fullname = `${contact.name ?? ''} ${contact.surname ?? ''}`.trim() || '—';
          return (
            <div
              key={contact.id}
              style={{
                border: '1px solid #f0f0f0',
                borderRadius: 6,
                padding: '6px 8px',
                background: '#fafafa',
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              <Text strong style={{ fontSize: 13 }} ellipsis={{ tooltip: fullname }}>
                {fullname}
              </Text>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '2px 10px',
                  color: '#595959',
                }}
              >
                {contact.email && (
                  <span>
                    <MailOutlined style={{ marginRight: 4 }} />
                    {contact.email}
                  </span>
                )}
                {contact.main_phone && (
                  <span>
                    <PhoneOutlined style={{ marginRight: 4 }} />
                    {formatPhone(contact.main_phone)}
                  </span>
                )}
              </div>
              {contact.producers && contact.producers.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  <TeamOutlined style={{ color: '#8c8c8c', marginRight: 4 }} />
                  <Space size={[4, 4]} wrap>
                    {contact.producers.slice(0, 3).map((p) => (
                      <Tag key={p.id} style={{ margin: 0, fontSize: 11 }}>
                        {p.name}
                      </Tag>
                    ))}
                    {contact.producers.length > 3 && (
                      <Tooltip
                        title={contact.producers
                          .slice(3)
                          .map((p) => p.name)
                          .join(', ')}
                      >
                        <Tag style={{ margin: 0, fontSize: 11 }}>
                          +{contact.producers.length - 3}
                        </Tag>
                      </Tooltip>
                    )}
                  </Space>
                </div>
              )}
              {(!contact.producers || contact.producers.length === 0) && (
                <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>
                  {t('admin.contacts.no_producers', 'Sin productores')}
                </Text>
              )}
              {/* Fechas: ayudan al admin a decidir qué contacto
                  conservar como primario al fusionar (más reciente,
                  más antiguo histórico, etc.). Hora completa en
                  tooltip; en línea sólo la fecha para no engordar la
                  card. Si created == modified, no repetimos. */}
              <div
                style={{
                  marginTop: 4,
                  fontSize: 11,
                  color: '#8c8c8c',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0 10px',
                }}
              >
                <Tooltip title={formatFull(contact.created)}>
                  <span>
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    {t('admin.contacts.created_short', 'Creado')}:{' '}
                    {formatShort(contact.created)}
                  </span>
                </Tooltip>
                {!wasNeverModified(contact) && (
                  <Tooltip title={formatFull(contact.modified)}>
                    <span>
                      <EditOutlined style={{ marginRight: 4 }} />
                      {t('admin.contacts.modified_short', 'Modif.')}:{' '}
                      {formatShort(contact.modified)}
                    </span>
                  </Tooltip>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer: botón de fusión a ancho completo */}
      <Button
        type="primary"
        icon={<MergeCellsOutlined />}
        onClick={onMerge}
        block
      >
        {t('admin.contacts.merge.action', 'Fusionar')}
      </Button>
    </Card>
  );
};
